
/**
 * --- External Imports ---
 */
import { IFormState } from "@ngt/forms-core";
import { useCallback, useContext } from "react";

/**
 * --- Internal Imports ---
*/
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { FormValidation, FormValidationType, IFormField, SpeciesForm, User } from "../../api/dtos/dtos";
import { getFieldType } from "../../components/forms/fields/species-fields/FormFieldComponent";
import SpeciesFormsContext from "../../contexts/data/SpeciesFormsContext";
import AuthContext from "../../contexts/auth/AuthContext";
import useFormFieldCondition from "./useFormFieldCondition";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export interface IValidatorOptions {
    softValidate?: boolean;
}

const defaultValidateOptions: IValidatorOptions = {
    softValidate: true
}

/**
 * --- Types ---
 */
interface IFieldValidator<TValidator extends IValidatorConfig = any> {
    (form: SpeciesForm, field: IFormField, value: any, validator: TValidator, user: User | null): boolean;
}

//
// -- Validator Configuration Types --
interface IValidatorConfig {
    validationType: FormValidationType
}

interface IRequiredValidatorConfig extends IValidatorConfig {
    validationType: FormValidationType.Required,
    meta: {
        targetName?: string;
        dateFormat?: string;
    }
}

interface IEqualValidatorConfig extends IValidatorConfig {
    validationType: FormValidationType.Equal,
    meta: {
        targetName?: string;
        value: string;
    }
}

interface INotEqualValidatorConfig extends IValidatorConfig {
    validationType: FormValidationType.NotEqual,
    meta: {
        targetName?: string;
        value: string;
    }
}

interface IGreaterThanValidatorConfig extends IValidatorConfig {
    validationType: FormValidationType.GreaterThan,
    meta: {
        targetName?: string;
        value: string;
    }
}

interface IGreaterThanOrEqualValidatorConfig extends IValidatorConfig {
    validationType: FormValidationType.GreaterThanOrEqual,
    meta: {
        targetName?: string;
        value: string;
    }
}

interface ILessThanValidatorConfig extends IValidatorConfig {
    validationType: FormValidationType.LessThan,
    meta: {
        targetName?: string;
        value: string;
    }
}

interface ILessThanOrEqualValidatorConfig extends IValidatorConfig {
    validationType: FormValidationType.LessThanOrEqual,
    meta: {
        targetName?: string;
        value: string;
    }
}

// interface IEmailValidatorConfig extends IValidatorConfig {
//     validationType: FormValidationType.Email
// }

interface IRegexValidatorConfig extends IValidatorConfig {
    validationType: FormValidationType.Regex,
    meta: {
        targetName?: string;
        expression: string
    }
}

interface ICustomValidatorConfig extends IValidatorConfig {
    validationType: FormValidationType.Custom,
    meta: {
        validator: string,
        [U: string]: string
    }
}

// Cast the value of the string to what ever the value should look like.
// This could be:
//  - string
//  - number
//  - boolean
// 
const castFieldValue = (field: IFormField, value: string): any => {
    switch(getFieldType(field)) {
        //
        // Cast date form fields into "Dayjs" objects. If the cast fails,
        // return null (consider it an empty value)
        case "DateFormField": 
            if (value == null || value === "") return null;
            const cast = dayjs(value);
            if (cast.isValid()) return cast
            return null;

        case "CheckboxFormField":
            return value !== null && value !== undefined && value;

        default:
            if (value == null) return null;
            return value;
    }
}

//
// --- Validators Record ---
//
const validators: Record<FormValidationType, IFieldValidator> = {
    [FormValidationType.Required]: (_, field, value, validator: IRequiredValidatorConfig, _user: User | null) => {
        const val = castFieldValue(field, value);
        
        // If the value is a "Dayjs", validate the object
        if (dayjs.isDayjs(val)) {
            return val.isValid() && dayjs(val, validator.meta?.dateFormat ?? 'dd/MM/yyyy', true).isValid();
        }

        switch (typeof val) {
            case "string": 
                return !!(val && val !== "");
            
            case "boolean":
                return val;
            // If the type of the value is any of these, we can assume that 
            // the value is valid (as the type reflects there is a value)
            case "number": 
            case "bigint":
            case "symbol":
                return true;
                
            // We assume if the value is any of these, then the value has
            // not been populated (we cannot return an object or a function)
            //
            // Also I was today-years-old (10/07/24) when I realised null maps
            // to an object...
            case "object":
            case "function":
            case "undefined":
                return false;
        }
    },
    [FormValidationType.Equal]: (_, field, value, validator: IEqualValidatorConfig, _user: User | null) => { 
        const val = castFieldValue(field, value);

        // If the value is a "Dayjs", validate the object and compare with validator
        if (dayjs.isDayjs(val)) {
            const target = dayjs(validator.meta.value);
            if (!target.isValid()) return false

            return val.isValid() && val.isSame(target);
        }

        switch (typeof val) {
            // We will assume the strings are an expected value, specified
            // in the meta
            case "string": 
                return val === validator.meta.value;
            
            // Cast the meta value to a number before comparing
            case "number": 
            case "bigint":
                return val === (+validator.meta.value)

            // Cast the meta value to a boolean before comparing
            case "boolean":
                return val === (validator.meta.value === "true");
                
            // At this point we don't have a universal way of comparing 
            // any of these primitive types. If we assume any of these
            // types are the value and want to compare them, use a custom
            // validator.
            case "symbol":
            case "object":
            case "function":
            case "undefined":
                return false;
        }
    },
    [FormValidationType.NotEqual]: (_, field, value, validator: INotEqualValidatorConfig, _user: User | null) => { 
        const val = castFieldValue(field, value);

        // If the value is a "Dayjs", validate the object and compare with validator
        if (dayjs.isDayjs(val)) {
            const target = dayjs(validator.meta.value);
            if (!target.isValid()) return false

            return val.isValid() && !val.isSame(target);
        }

        switch (typeof val) {
            // We will assume the strings are an expected value, specified
            // in the meta
            case "string": 
                return val !== validator.meta.value;
            
            // Cast the meta value to a number before comparing
            case "number": 
            case "bigint":
                return val !== (+validator.meta.value)

            // Cast the meta value to a boolean before comparing
            case "boolean":
                return val !== (validator.meta.value === "true");
                
            // At this point we don't have a universal way of comparing 
            // any of these primitive types. If we assume any of these
            // types are the value and want to compare them, use a custom
            // validator.
            case "symbol":
            case "object":
            case "function":
            case "undefined":
                return false;
        }
    },
    [FormValidationType.GreaterThan]: (_, field, value, validator: IGreaterThanValidatorConfig, _user: User | null) => { 
        const val = castFieldValue(field, value);

        // If the value is a "Dayjs", validate the object and compare with validator
        if (dayjs.isDayjs(val)) {
            const target = dayjs(validator.meta.value);
            if (!target.isValid()) return false

            return val.isValid() && val.isAfter(target);
        }

        switch (typeof value) {
            // It should be noted that the behaviour of comparing the strings this
            // way is unknown.
            case "string": 
                return value > validator.meta.value;
            
            // Cast the meta value to a number before comparing
            case "number": 
            case "bigint":
                return value > (+validator.meta.value)

            // At this point we don't have a universal way of comparing 
            // any of these primitive types. If we assume any of these
            // types are the value and want to compare them, use a custom
            // validator.
            case "boolean":
            case "symbol":
            case "object":
            case "function":
            case "undefined":
                return false;
        }    
    },
    [FormValidationType.GreaterThanOrEqual]: (_, field, value, validator: IGreaterThanOrEqualValidatorConfig, _user: User | null) => { 
        const val = castFieldValue(field, value);

        // If the value is a "Dayjs", validate the object and compare with validator
        if (dayjs.isDayjs(val)) {
            const target = dayjs(validator.meta.value);
            if (!target.isValid()) return false

            return val.isValid() && val.isSameOrAfter(target);
        }

        switch (typeof value) {
            // It should be noted that the behaviour of comparing the strings this
            // way is unknown.
            case "string": 
                return value >= validator.meta.value;
            
            // Cast the meta value to a number before comparing
            case "number": 
            case "bigint":
                return value >= (+validator.meta.value)

            // At this point we don't have a universal way of comparing 
            // any of these primitive types. If we assume any of these
            // types are the value and want to compare them, use a custom
            // validator.
            case "boolean":
            case "symbol":
            case "object":
            case "function":
            case "undefined":
                return false;
        } 
    },
    [FormValidationType.LessThan]: (_, field, value, validator: ILessThanValidatorConfig, _user: User | null) => {
        const val = castFieldValue(field, value);

        // If the value is a "Dayjs", validate the object and compare with validator
        if (dayjs.isDayjs(val)) {
            const target = dayjs(validator.meta.value);
            if (!target.isValid()) return false

            return val.isValid() && val.isBefore(target);
        }

        switch (typeof value) {
            // It should be noted that the behaviour of comparing the strings this
            // way is unknown.
            case "string": 
                return value < validator.meta.value;
            
            // Cast the meta value to a number before comparing
            case "number": 
            case "bigint":
                return value < (+validator.meta.value)

            // At this point we don't have a universal way of comparing 
            // any of these primitive types. If we assume any of these
            // types are the value and want to compare them, use a custom
            // validator.
            case "boolean":
            case "symbol":
            case "object":
            case "function":
            case "undefined":
                return false;
        } 
    },
    [FormValidationType.LessThanOrEqual]: (_, field, value, validator: ILessThanOrEqualValidatorConfig, _user: User | null) => {
        const val = castFieldValue(field, value);

        // If the value is a "Dayjs", validate the object and compare with validator
        if (dayjs.isDayjs(val)) {
            const target = dayjs(validator.meta.value);
            if (!target.isValid()) return false

            return val.isValid() && val.isSameOrBefore(target);
        }

        switch (typeof value) {
            // It should be noted that the behaviour of comparing the strings this
            // way is unknown.
            case "string": 
                return value <= validator.meta.value;
            
            // Cast the meta value to a number before comparing
            case "number": 
            case "bigint":
                return value <= (+validator.meta.value)

            // At this point we don't have a universal way of comparing 
            // any of these primitive types. If we assume any of these
            // types are the value and want to compare them, use a custom
            // validator.
            case "boolean":
            case "symbol":
            case "object":
            case "function":
            case "undefined":
                return false;
        } 
    },
    [FormValidationType.Email]: (_, __, value, ___, _user: User | null) => {
        switch (typeof value) {
            // Validate the input is a string and is of email format.
            case "string": 
                const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/;
                return emailRegex.test(value);
            
            // The only type this validation works for is strings. We cannot support 
            // any other primitive types. If we need to, try making a custom validator
            case "number": 
            case "bigint":
            case "boolean":
            case "symbol":
            case "object":
            case "function":
            case "undefined":
                return false;
        } 
    },
    [FormValidationType.Regex]: (_, __, value, validator: IRegexValidatorConfig, _user: User | null) => {
        switch (typeof value) {
            // Validate the input is a string and is of email format.
            case "string": 
                const emailRegex = new RegExp(validator.meta.expression);
                return emailRegex.test(value);
            
            // The only type this validation works for is strings. We cannot support 
            // any other primitive types. If we need to, try making a custom validator
            case "number": 
            case "bigint":
            case "boolean":
            case "symbol":
            case "object":
            case "function":
            case "undefined":
                return false;
        }
    },
    [FormValidationType.Custom]: (form: SpeciesForm, field: IFormField, value: any, validator: ICustomValidatorConfig, user: User | null) => {
        // Call a custom validator, specified in the validator config. The result will be
        // the output of the custom validation function OR false if the function was not found.
        //
        // All custom validator functions need to be added to the custom validators record.
        return callCustomValidator(form, field, value, validator, user);
    },
}

//
// --- Custom Function ---
//
const UnauthenticatedUser = (_form: SpeciesForm, _field: IFormField, _value: any, _validator: ICustomValidatorConfig, user: User | null) => {
    if (!user){
        return true;
    }
    return false;
};
const AuthenticatedUser = (_form: SpeciesForm, _field: IFormField, _value: any, _validator: ICustomValidatorConfig, user: User | null) => {
    if (user){
        return true;
    }
    return false;
};

const CoordinatesState = (_form: SpeciesForm, _field: IFormField, value: any, validator: ICustomValidatorConfig, _user: User | null) => {
    if (!value || !validator){
        return false;
    }

    if (value['state'] === validator.meta['state']){
        return false;
    }
    
    return true;
};

//
// --- Custom Validation ---
//
const customValidators: Record<string, IFieldValidator> = {
    "UnauthenticatedUser": UnauthenticatedUser,
    "AuthenticatedUser": AuthenticatedUser,
    "CoordinatesState": CoordinatesState
}

const callCustomValidator = (form: SpeciesForm, field: IFormField, value: any, validator: ICustomValidatorConfig, user: User | null): boolean => {
    const customFunction = customValidators[validator.meta.validator];

    if (customFunction) {
        return customFunction(form, field, value, validator, user);
    }

    return false;
}

/**
 * --- Hook ---
 */
export const useValidateSpeciesForm = () => {
    const { selectedForm, selectedFormFields } = useContext(SpeciesFormsContext);
    const { user } = useContext(AuthContext);
    const { getFormFieldDisplayConditionValue } = useFormFieldCondition();

    const execChilrenValidation = (key: string, form: SpeciesForm, selectedFormFields: Record<string, IFormField>, formState: IFormState<any, string>,  childValidators: FormValidation[]): string | null => {
        const field = selectedFormFields[key];

        let ret: string | null = null;

        for (let val of childValidators) {

            const validator = validators[val.validationType];

            const targetName = val.meta?.targetName;
            const validatorField = targetName ? selectedFormFields[targetName] : field
            const validatorFieldValue = targetName ? formState.value[form.name]?.[targetName] : formState.value[form.name]?.[field.name!];

            let childrenRes: string | null = execChilrenValidation(key, form, selectedFormFields, formState, val.children ?? []);
            let res: boolean = validator(form, validatorField, validatorFieldValue, val, user) && !(childrenRes);
            
            if (!res || !!(childrenRes)) {
                ret = val.meta?.message ?? 
                    ret ?? 
                    childrenRes ?? 
                    `${key} is required`;
            }
            else {
                ret = null;
                break;
            }
        }

        return ret;
    }

    // Validate a field in the corrent species form. If the validation succeeds, the response will be
    // null. Otherwise a string will be returned with a message of the error (if the error is specified)
    // 
    const execValidation = (key: string, form: SpeciesForm, selectedFormFields: Record<string, IFormField>, formState: IFormState<any, string>) => {
        // console.log('==> v1', )
        // Iterate the form validations for this field and exec in an order.
        // If one of the validations fails, return immediately (we can fix this
        // later if we desire to have multiple error messages for a field).

        const field = selectedFormFields[key];
        let ret: string | null = null;

        for (let val of (field.validation ?? [])) {
            const validator = validators[val.validationType];

            const targetName = val.meta?.targetName;
            const validatorField = targetName ? selectedFormFields[targetName] : field;
            const validatorFieldValue = targetName ? formState.value[form.name]?.[targetName] : formState.value[form.name]?.[field.name!];

            let childrenRes: string | null = execChilrenValidation(key, form, selectedFormFields, formState, val.children ?? []);
            let res: boolean = validator(form, validatorField, validatorFieldValue, val, user) && !(childrenRes);
            if (!res) {
                ret = val.meta?.message ?? 
                    ret ?? 
                    childrenRes ?? 
                    `${key} is required`;
            }
            else {
                ret = null;
                break;
            }
        }

        return ret;
    }

    const validateSpeciesFormField = useCallback((key: string, formState: IFormState<any, string>) => {
        // We require by this point that a form has been selected and a state has been 
        // initiated in the "FormState".
        if (!selectedForm || !selectedFormFields) return null;
        if (!formState?.value?.[selectedForm.name]) return null;

        const field = selectedFormFields[key];

        // If the field does not have validation, assume validation passed.
        if (!field) return null;

        // If the field is not displayed, don't validate it.
        const isDisplayed = getFormFieldDisplayConditionValue(field, formState);
        if (!isDisplayed) {
            return null;
        }

        return execValidation(key, selectedForm, selectedFormFields, formState);
    }, [selectedForm, selectedFormFields, execValidation]);

    const getSoftValidateKeys = (formState: IFormState<any, string>, selectedForm: SpeciesForm) => {
        let keys: string[] = [];
        keys = Object.keys(formState.touched)
                .filter(x => x !== selectedForm.name)
                .map(x => x.replace(`${selectedForm.name}.`, ""));
        
        keys.forEach((k) => console.log(k));
        // TODO: 
        // loop keys 
        // check if each key is a checkbox
        // if it is a checkbox, check if it has a checkboxGroupId
        // if it has a checkboxGroupId, get the checkbox group
        // add the checkbox groups name to keys

        return keys;
    }

    const validateSpeciesForm = useCallback((formState: IFormState<any, string>, { softValidate }: IValidatorOptions = defaultValidateOptions) => {
        if (!selectedForm || !selectedFormFields) return null;
        if (!(formState?.value?.[selectedForm.name])) return null;

        const newState: Record<string, string[]> = {};

        let keys: string[] = [];

        // If we are softly validating, we only want to validate fields that have been
        // "touched". Else, we want to run validation on all fields to verify the 
        // form requirements have been met.
        if (softValidate) {
            keys = getSoftValidateKeys(formState, selectedForm);
        } else {
            keys = Object.keys(selectedFormFields);
        }
        
        // Iterate the list of field keys that require validation
        for (let key of keys) {
            const state = validateSpeciesFormField(key, formState);

            if (state) {
                newState[`${selectedForm.name}.${key}`] = [
                    state
                ]
            }
        }

        return newState;
    }, [selectedForm, selectedFormFields]);

    return {
        validateSpeciesForm,
        validateSpeciesFormField
    };
}


export default useValidateSpeciesForm;